import format from 'date-fns/format';
import { unparse } from 'papaparse';
import fileDownload from 'js-file-download';
import { parseDonationAmount } from 'common/utils';

import { donationBillingIntervalLabels, donationStatusLabels, donationTypeLabels } from '../constants';
import { escapeRegExp } from './helpers';

export const filterDonations = (donations, filters = {}) => {
  const { startDate, endDate, minAmount, maxAmount, type, status, campaignIds, designationIds, searchTerm } = filters;

  let filteredDonations = [...donations];

  if (startDate) {
    filteredDonations = filteredDonations.filter((don) => {
      const startTime = Math.round(new Date(startDate).getTime() / 1000);
      const createdAt = parseInt(don.createdAt, 10);
      return createdAt >= startTime;
    });
  }

  if (endDate) {
    filteredDonations = filteredDonations.filter((don) => {
      const endTime = Math.round(new Date(endDate).getTime() / 1000);
      const createdAt = parseInt(don.createdAt, 10);
      return createdAt <= endTime;
    });
  }

  if (campaignIds && campaignIds.length > 0) {
    filteredDonations = filteredDonations.filter((don) => campaignIds.includes(don.campaignId));
  }
  if (designationIds && designationIds.length > 0) {
    filteredDonations = filteredDonations.filter((don) => designationIds.includes(don.designationId));
  }
  if (minAmount) {
    filteredDonations = filteredDonations.filter((don) => don.netAmount / 100 >= minAmount);
  }
  if (maxAmount) {
    filteredDonations = filteredDonations.filter((don) => don.netAmount / 100 <= maxAmount);
  }
  if (searchTerm) {
    filteredDonations = filteredDonations.filter((don) =>
      don.donorName.match(new RegExp(escapeRegExp(searchTerm), 'i')),
    );
  }
  if (status) {
    filteredDonations = filteredDonations.filter((don) => don.status === status);
  }
  if (type) {
    filteredDonations = filteredDonations.filter((don) => don.type === type);
  }

  return filteredDonations;
};

const DONATION_COLUMNS = {
  donationId: 'Donation ID',
  donorId: 'Donor ID',
  createdAt: 'Date',
  netAmount: 'Amount',
  type: 'Type',
  status: 'Status',
  donorName: 'Donor',
  donorEmail: 'Donor Email',
  campaignName: 'Campaign',
  amount: 'Gross Amount',
  coverFee: 'Covered Fees',
  stripeFeeAmount: 'Covered Fee Amount',
  tribute: 'Tribute',
  comment: 'Comment',
  source: 'Source',
  referrer: 'Referrer',
  designationName: 'Designation Name',
  designationCode: 'Designation Code',
};

export const downloadDonationsFile = (donations, filename) => {
  const data = donations.map((donation) => {
    const donationData = {};
    Object.keys(DONATION_COLUMNS).forEach((column) => {
      const objKey = DONATION_COLUMNS[column];
      const objValue = donation[column];
      if (column === 'createdAt') {
        donationData[objKey] = objValue ? format(new Date(objValue * 1000), 'MM.dd.yyyy') : '';
        return;
      }
      if (column === 'netAmount') {
        donationData[objKey] = objValue ? parseDonationAmount(objValue / 100) : '';
        return;
      }
      if (column === 'status') {
        donationData[objKey] = donationStatusLabels[objValue] || '';
        return;
      }
      if (column === 'type') {
        let label = donationTypeLabels[objValue];
        if (donation.paymentInfo?.subscription?.status) {
          const intervalLabel = ` ${donationBillingIntervalLabels[donation.billingInterval] || donationBillingIntervalLabels.month}`;
          label += `${intervalLabel} - ${donation.paymentInfo.subscription.status.toUpperCase()}`;
        }
        donationData[objKey] = label;
        return;
      }
      if (column === 'amount') {
        donationData[objKey] = objValue ? parseDonationAmount(objValue / 100) : '';
        return;
      }
      if (column === 'coverFee') {
        donationData[objKey] = objValue === 'true' ? 'Yes' : 'No';
        return;
      }
      if (column === 'stripeFeeAmount') {
        const { amount, coverFee } = donation;
        const stripeFeeAmount = objValue;
        if (coverFee !== 'true') {
          donationData[objKey] = parseDonationAmount(0);
        } else {
          const coveredFeeAmount = amount - (amount - stripeFeeAmount) / 1.015; // Covered fee is capped at 1.5%
          donationData[objKey] = parseDonationAmount(coveredFeeAmount / 100); // amount is in cents
        }
        return;
      }
      if (column === 'tribute') {
        const { tribute } = donation;
        donationData[objKey] = tribute?.enabled ? `In ${tribute?.type} of ${tribute?.honoreeName}` : '';
        return;
      }
      if (column === 'comment') {
        const { comment } = donation;
        donationData[objKey] = comment?.enabled ? comment?.text : '';
        return;
      }
      donationData[objKey] = objValue;
    });
    return donationData;
  });
  const csvData = unparse(data);
  const blob = new Blob([csvData], { type: 'text/csv' });
  fileDownload(blob, filename);
};
